import { FC, useEffect, useState } from 'react';

import RU from 'date-fns/locale/ru';
import { MagritteNotificationName } from 'utils/constants/notification';
import { formatDate, getDateFromString, previousMonth, TODAY_START_OF_DAY } from 'utils/date';
import { fileDownload } from 'utils/fileDownload';
import { isAndroid, isIOS } from 'utils/platform';

import dashboardDownloadStatisticButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_download_statistic_button_click';
import { dashboardDownloadStatisticButtonShownRaw } from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_download_statistic_button_shown';
import { dashboardDownloadStatisticErrorShownRaw } from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_download_statistic_error_shown';
import dashboardDownloadStatisticFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_download_statistic_form_submit';
import {
    ActionBar,
    BottomSheet,
    Button,
    Card,
    DateType,
    IntervalDatePicker,
    Link,
    Modal,
    Text,
    useBreakpoint,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { ArrowDownLineOutlinedSize16, CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import BlokoButton, { ButtonIconPosition } from 'bloko/blocks/button';
import Icon, { IconScale, IconView } from 'bloko/blocks/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import withErrorBoundary from 'components/common/ErrorBoundary/withErrorBoundary';
import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';

import { addMagritteNotification } from 'store/models/notification';
import { useDispatch } from 'store/utils';

const START_OF_MONTH = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
const MIN_DATE = getDateFromString('2020-01-01') as Date;
const MAX_DATE = TODAY_START_OF_DAY;
const DEFAULT_DATE_INTERVAL: DownloadStatisticDateInterval = { from: START_OF_MONTH, to: TODAY_START_OF_DAY };

const TrlKeys = {
    button: 'dashboard.title.downloadStatistic.button',
    modalTitle: 'dashboard.title.downloadStatistic.modal.title',
    descriptionTop: 'dashboard.title.downloadStatistic.modal.descriptionTop',
    email: 'dashboard.title.downloadStatistic.modal.email',
    emailSubject: 'dashboard.title.downloadStatistic.modal.email.subject',
    reviewLink: 'dashboard.title.downloadStatistic.modal.reviewLink',
    modalButton: 'dashboard.title.downloadStatistic.modal.button',
    IS_EMPTY_TO_DATE: 'dashboard.title.downloadStatistic.modal.validationError.IS_EMPTY_TO_DATE',
};

export interface DownloadStatisticDateInterval {
    from: Date | DateType | null;
    to: Date | DateType | null;
}

interface DownloadStatisticButtonProps {
    isDashboardRedesign?: boolean;
}

const DownloadStatisticButtonComponent: FC<DownloadStatisticButtonProps> = ({ isDashboardRedesign = false }) => {
    const trl = useTrls();
    const dispatch = useDispatch();
    const { isMobile } = useBreakpoint();

    const DEFAULT_DISPLAY_DATE = isMobile ? MAX_DATE : previousMonth;

    const [isOpenModal, setOpenModalOn, setOpenModalOff] = useOnOffState(false);
    const [dateInterval, setDateInterval] = useState<DownloadStatisticDateInterval>(DEFAULT_DATE_INTERVAL);
    const [displayDate, setDisplayDate] = useState<Date | DateType | undefined>(DEFAULT_DISPLAY_DATE);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        dashboardDownloadStatisticButtonShownRaw();
    }, []);

    const resetState = () => {
        setDateInterval(DEFAULT_DATE_INTERVAL);
        setDisplayDate(DEFAULT_DISPLAY_DATE);
        setValidationError(null);
    };

    const downloadStatisticHandleClick = () => {
        setOpenModalOn();
        dashboardDownloadStatisticButtonClick();
    };

    const handleDownload = async () => {
        const startDate = dateInterval.from ? formatDate(new Date(dateInterval.from)) : undefined;
        const endDate = dateInterval.to ? formatDate(new Date(dateInterval.to)) : undefined;
        const errorMessage = endDate ? null : trl(TrlKeys.IS_EMPTY_TO_DATE);

        setValidationError(errorMessage);

        dashboardDownloadStatisticFormSubmit({
            startDate: startDate ?? '',
            endDate: endDate ?? '',
            errors: errorMessage,
        });

        if (!errorMessage && startDate && endDate) {
            const url = `/api/v1/campaigns/statistic?from=${startDate}&to=${endDate}`;

            if (isAndroid() || isIOS()) {
                window.open(url);
                setOpenModalOff();
                return;
            }

            try {
                setIsLoading(true);
                await fileDownload(url);
                setOpenModalOff();
            } catch (e) {
                dispatch(addMagritteNotification(MagritteNotificationName.DownloadStatisticError));
                dashboardDownloadStatisticErrorShownRaw();
                return;
            } finally {
                setIsLoading(false);
            }
        }
    };

    const feedbackEmail = `${trl(TrlKeys.email)}?subject=${trl(TrlKeys.emailSubject)}`;

    const closeAction = <CrossOutlinedSize24 initialColor="secondary" onClick={setOpenModalOff} />;

    const footer = (
        <ActionBar
            secondaryActions={
                <Link href={`mailto:${feedbackEmail}`} inline>
                    {trl(TrlKeys.reviewLink)}
                </Link>
            }
            primaryActions={
                <Button
                    mode="primary"
                    style="accent"
                    icon={<ArrowDownLineOutlinedSize16 />}
                    onClick={handleDownload}
                    data-qa="download-statistic-submit"
                    loading={isLoading}
                >
                    {trl(TrlKeys.modalButton)}
                </Button>
            }
        />
    );

    const content = (
        <>
            <Text>{trl(TrlKeys.descriptionTop)}</Text>
            <VSpacing default={24} />
            <Card borderWidth="default" stretched borderRadius={12}>
                <IntervalDatePicker
                    layout={isMobile ? 'single' : 'multi'}
                    locale={RU}
                    enabledCalendars="dd.mm.yyyy"
                    selectionStart={dateInterval.from}
                    selectionEnd={dateInterval.to}
                    minDate={MIN_DATE}
                    maxDate={MAX_DATE}
                    displayDate={displayDate}
                    onDisplayDateChange={setDisplayDate}
                    onStartSelect={(from) => setDateInterval((prevState) => ({ ...prevState, from }))}
                    onEndSelect={(to) => {
                        setDateInterval((prevState) => ({ ...prevState, to }));
                        setValidationError(null);
                    }}
                />
            </Card>
            {!!validationError && (
                <>
                    <VSpacing default={12} />
                    <Text typography="label-3-regular" style="negative">
                        {validationError}
                    </Text>
                </>
            )}
        </>
    );

    return (
        <>
            {isDashboardRedesign ? (
                <Link
                    data-qa="download-statistic"
                    Element="button"
                    onClick={downloadStatisticHandleClick}
                    iconRight={<ArrowDownLineOutlinedSize16 />}
                >
                    {trl(TrlKeys.button)}
                </Link>
            ) : (
                <BlokoButton
                    onClick={downloadStatisticHandleClick}
                    data-qa="download-statistic"
                    icon={<Icon view={IconView.ArrowLine} scale={IconScale.Small} />}
                    iconPosition={ButtonIconPosition.Left}
                >
                    {!isMobile ? trl(TrlKeys.button) : undefined}
                </BlokoButton>
            )}
            {isMobile ? (
                <BottomSheet
                    visible={isOpenModal}
                    onClose={setOpenModalOff}
                    onAfterExit={resetState}
                    height="content"
                    footer={footer}
                    header={<MagritteModalHeader title={trl(TrlKeys.modalTitle)} actions={closeAction} size="small" />}
                    data-qa="download-statistic-modal"
                >
                    {content}
                </BottomSheet>
            ) : (
                <Modal
                    visible={isOpenModal}
                    onClose={setOpenModalOff}
                    onAfterExit={resetState}
                    size="large"
                    header={<MagritteModalHeader title={trl(TrlKeys.modalTitle)} actions={closeAction} />}
                    footer={footer}
                    data-qa="download-statistic-modal"
                >
                    {content}
                </Modal>
            )}
        </>
    );
};

export const DownloadStatisticButton = withErrorBoundary(DownloadStatisticButtonComponent);
